import type { FC } from 'react'
import { memo } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@fp/ui-kit'
import { formatDate, type DateFormat } from '@lib/formatters'

type DateValue = string | null | undefined

type ShowDataAndTooltipProps = {
  data: DateValue | DateValue[]
  format?: DateFormat
  labels?: string[]
  /** Defaults to the app timezone; pass `UTC_TIMEZONE` to render the raw server instant instead. */
  timeZone?: string
}

export const ShowDataAndTooltip: FC<ShowDataAndTooltipProps> = memo(({ data, format, labels, timeZone }) => {
  const values = Array.isArray(data) ? data : [data]

  if (!values.some(Boolean)) return '—'

  const [firstValue] = values

  if (!format && values.length === 1 && firstValue) {
    return (
      <span className='inline-flex flex-col items-center leading-4'>
        {formatDate(firstValue, 'short', timeZone)}
        <span className='text-xs text-muted-foreground'>{formatDate(firstValue, 'timeShort', timeZone)}</span>
      </span>
    )
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger>
        {values.map((d) => (d ? formatDate(d, format ?? 'short', timeZone) : '—')).join(' / ')}
      </TooltipTrigger>
      <TooltipContent>
        {values.map((d, i) => (
          <p key={`${i}-${d ?? 'none'}`}>
            {labels?.[i] ? `${labels[i]}: ` : ''}
            {d ? formatDate(d, 'fullWithZone', timeZone) : '—'}
          </p>
        ))}
      </TooltipContent>
    </Tooltip>
  )
})

ShowDataAndTooltip.displayName = 'ShowDataAndTooltip'
